import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.forgefair.chxse.dev/api';

// Create an axios instance with default config
const api = axios.create({
  baseURL: API_URL,
});

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  // Get the access token from Auth0
  const token = localStorage.getItem('auth0_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface Project {
  _id: string;
  title: string;
  description: string;
  imageUrl?: string;
  price?: number;
  status: 'draft' | 'active' | 'sold' | 'archived';
  category?: string;
  tags?: string[];
  userId: string;
  createdAt: string;
  updatedAt: string;
  revenueStatus?: 'pre-revenue' | 'revenue' | 'na';
  monthlyRevenue?: number;
  annualRevenue?: number;
  userCount?: number;
  ytdGrowth?: number;
  projectType: 'saas' | 'mobile' | 'web' | 'desktop' | 'other';
  licensing: {
    includeSourceCode: boolean;
    transferOwnership: boolean;
  };
}

export interface ProjectFilters {
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'price' | 'title';
  sortOrder?: 'asc' | 'desc';
  category?: string;
  tags?: string[];
  minPrice?: number;
  maxPrice?: number;
  status?: 'active' | 'sold' | 'draft';
  revenueStatus?: 'pre-revenue' | 'revenue' | 'na';
  search?: string;
}

export interface CreateProjectData {
  title: string;
  description: string;
  imageUrl?: string;
  price?: number;
  projectType: string;
  revenueStatus: string;
  licensing: {
    includeSourceCode: boolean;
    transferOwnership: boolean;
  };
}

export const projectService = {
  async getProjects(filters: ProjectFilters = {}) {
    const response = await api.get('/projects', { params: filters });
    return response.data;
  },

  async getProjectById(id: string) {
    const response = await api.get(`/projects/${id}`);
    return response.data;
  },

  async createProject(data: CreateProjectData) {
    console.log('Creating project with data:', data);
    const response = await api.post('/projects', data);
    return response.data;
  },

  async uploadImage(file: File) {
    console.log('Starting image upload...');
    const formData = new FormData();
    formData.append('image', file);

    // Get a fresh token
    const token = localStorage.getItem('auth0_token');
    console.log('Auth token present:', !!token);

    const response = await api.post('/projects/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`
      },
    });

    console.log('Image upload response:', response.data);
    return response.data.imageUrl;
  },

  async updateProject(id: string, projectData: Partial<Project>): Promise<Project> {
    const response = await api.put(`/projects/${id}`, projectData);
    return response.data;
  },

  async deleteProject(id: string): Promise<void> {
    await api.delete(`/projects/${id}`);
  },

  async placeBid(projectId: string, amount: number): Promise<void> {
    await api.post(`/projects/${projectId}/bids`, { amount });
  },

  async getProjectBids(projectId: string): Promise<any[]> {
    const response = await api.get(`/projects/${projectId}/bids`);
    return response.data;
  },
}; 